/**
 * AssetLoader - Handles loading all game assets before gameplay begins
 * Loads fonts, initializes audio buffers, and reports progress.
 *
 * Assets loaded:
 *   - Google Font: "Share Tech Mono"
 *   - Procedural audio buffers via Web Audio API
 */

export class AssetLoader {
  constructor() {
    this.totalAssets = 0;
    this.loadedAssets = 0;
    this.errors = [];
    this.loaded = false;
    this.loading = false;
  }

  /**
   * Get loading progress as 0.0 - 1.0.
   */
  getProgress() {
    if (this.totalAssets === 0) return 0;
    return this.loadedAssets / this.totalAssets;
  }

  /**
   * Load all game assets. Returns a promise that resolves when all assets are loaded.
   * @param {AudioManager} audioManager - The audio manager to initialize sounds on
   */
  async loadAll(audioManager) {
    if (this.loading || this.loaded) return;
    this.loading = true;

    // Define asset loading tasks
    const tasks = [
      { name: 'font', fn: () => this._loadFont() },
      { name: 'audio_init', fn: () => this._initAudio(audioManager) },
      { name: 'audio_sounds', fn: () => this._generateSounds(audioManager) },
    ];

    this.totalAssets = tasks.length;
    this.loadedAssets = 0;

    for (const task of tasks) {
      try {
        await task.fn();
        this.loadedAssets++;
        console.log(`Asset loaded: ${task.name} (${this.loadedAssets}/${this.totalAssets})`);
      } catch (e) {
        // Log error but don't block - count as loaded to not stall
        console.warn(`Asset load warning for ${task.name}:`, e.message || e);
        this.errors.push({ name: task.name, error: e });
        this.loadedAssets++;
      }
    }

    this.loaded = true;
    this.loading = false;
    console.log(`All assets loaded. ${this.errors.length} warnings.`);
  }

  /**
   * Load the game font using the CSS Font Loading API.
   */
  async _loadFont() {
    if (!document.fonts) {
      // Font Loading API not available, font will load via CSS fallback
      console.log('Font Loading API not available, using CSS fallback.');
      return;
    }

    try {
      // The font is loaded via CSS @import in main.css from Google Fonts
      // We wait for it to be ready
      const font = new FontFace(
        'Share Tech Mono',
        'url(https://fonts.gstatic.com/s/sharetechmono/v15/J7aHnp1uDWRBEqV98dVQztYldFcLowEF.woff2)',
        { style: 'normal', weight: '400' }
      );

      const loadedFont = await font.load();
      document.fonts.add(loadedFont);
      console.log('Font "Share Tech Mono" loaded successfully.');
    } catch (e) {
      // Font may already be loaded via CSS, which is fine
      console.log('Font load via JS fallback, CSS import may handle it.');
    }
  }

  /**
   * Initialize the Web Audio API context.
   */
  async _initAudio(audioManager) {
    // The AudioContext can only be created/resumed after user interaction
    // in many browsers. We prepare it here but it may need to be resumed later.
    try {
      if (!audioManager.context) {
        audioManager.context = new (window.AudioContext || window.webkitAudioContext)();
        audioManager.masterGain = audioManager.context.createGain();
        audioManager.masterGain.gain.value = audioManager.masterVolume;
        audioManager.masterGain.connect(audioManager.context.destination);
      }
      audioManager.initialized = true;
      console.log('Audio context created successfully.');
    } catch (e) {
      console.warn('Web Audio API initialization warning:', e);
      // Not a fatal error - game can run without audio
    }
  }

  /**
   * Generate procedural sound effects using Web Audio API.
   * Stores AudioBuffer objects on the audioManager.
   */
  async _generateSounds(audioManager) {
    if (!audioManager.context) {
      console.log('No audio context available, skipping sound generation.');
      return;
    }

    const ctx = audioManager.context;
    const sampleRate = ctx.sampleRate;

    // Generate each sound effect as an AudioBuffer
    // Sound design inspired by classic arcade SFX from OpenGameArt.org
    const soundDefs = {
      launch: () => this._genLaunchSound(ctx, sampleRate),
      explosion_small: () => this._genExplosionSound(ctx, sampleRate, 0.3),
      explosion_medium: () => this._genExplosionSound(ctx, sampleRate, 0.5),
      explosion_large: () => this._genExplosionSound(ctx, sampleRate, 0.8),
      city_destroy: () => this._genCityDestroySound(ctx, sampleRate),
      wave_complete: () => this._genWaveCompleteSound(ctx, sampleRate),
      empty_click: () => this._genEmptyClickSound(ctx, sampleRate),
      ambient: () => this._genAmbientSound(ctx, sampleRate),
      mirv_split: () => this._genMirvSplitSound(ctx, sampleRate),
      missile_incoming: () => this._genMissileIncomingSound(ctx, sampleRate),
      score_tick: () => this._genScoreTickSound(ctx, sampleRate),
      game_over: () => this._genGameOverSound(ctx, sampleRate),
    };

    for (const [name, genFn] of Object.entries(soundDefs)) {
      try {
        const buffer = genFn();
        audioManager.sounds[name] = buffer;
      } catch (e) {
        console.warn(`Sound generation warning for ${name}:`, e);
      }
    }

    console.log(`Generated ${Object.keys(audioManager.sounds).length} procedural sounds.`);
  }

  // ─── Utility: Simple one-pole low-pass filter state ───
  _lpf(prev, sample, cutoff) {
    return prev + cutoff * (sample - prev);
  }

  // ─── Utility: ADSR envelope ───
  _adsr(t, attack, decay, sustain, release, duration) {
    if (t < attack) return t / attack;
    if (t < attack + decay) return 1.0 - (1.0 - sustain) * ((t - attack) / decay);
    if (t < duration - release) return sustain;
    if (t < duration) return sustain * (1.0 - (t - (duration - release)) / release);
    return 0;
  }

  // ─── Utility: Soft clipping (tanh saturation) ───
  _softClip(x) {
    return Math.tanh(x);
  }

  /**
   * Generate a "launch" sound - rocket ignition whoosh with filtered noise,
   * ascending-then-descending pitch sweep, and stereo phaser-like movement.
   * Inspired by classic arcade missile launch SFX from OpenGameArt.
   */
  _genLaunchSound(ctx, sampleRate) {
    const duration = 0.45;
    const buffer = ctx.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);
    let lpState = 0;

    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;

      // Two-phase envelope: quick rise then exponential fall
      const env = (t < 0.02 ? t / 0.02 : 1.0) * Math.exp(-t * 6);

      // Frequency sweep: rises briefly then descends (rocket launch feel)
      const freqRise = t < 0.06 ? 400 + (t / 0.06) * 600 : 1000;
      const freqFall = t >= 0.06 ? freqRise * Math.exp(-(t - 0.06) * 8) + 80 : freqRise;
      const freq = Math.max(freqFall, 80);

      // Main oscillator with slight FM modulation for richness
      const modulator = Math.sin(2 * Math.PI * freq * 1.5 * t) * 0.3;
      const carrier = Math.sin(2 * Math.PI * freq * t + modulator);

      // Filtered noise layer (rocket thrust)
      const rawNoise = Math.random() * 2 - 1;
      const noiseCutoff = Math.min(0.8, 0.1 + t * 3) * Math.exp(-t * 4);
      lpState = this._lpf(lpState, rawNoise, noiseCutoff);
      const filteredNoise = lpState * 3.0; // Amplify filtered noise

      // Transient click at ignition
      const ignition = t < 0.01 ? Math.sin(2 * Math.PI * 2000 * t) * (1 - t / 0.01) : 0;

      // Mix
      const sample = env * (carrier * 0.35 + filteredNoise * 0.45) + ignition * 0.15;
      data[i] = this._softClip(sample * 0.7);
    }

    return buffer;
  }

  /**
   * Generate an explosion sound with variable intensity.
   * Uses layered noise bands, sub-bass thump, mid-freq resonance,
   * and debris tail for rich, varied explosions.
   * Inspired by classic arcade explosion SFX.
   */
  _genExplosionSound(ctx, sampleRate, intensity) {
    const duration = 0.5 + intensity * 0.7;
    const buffer = ctx.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);
    let lpState1 = 0, lpState2 = 0, hpState = 0;

    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;

      // === PHASE 1: Initial transient (0-30ms) ===
      const transientEnv = t < 0.03 ? (1 - t / 0.03) : 0;
      const transient = transientEnv * Math.sin(2 * Math.PI * (150 + intensity * 100) * t);

      // === PHASE 2: Main blast body ===
      const blastEnv = (t < 0.01 ? t / 0.01 : 1) * Math.exp(-t * (2.5 - intensity * 1.5));

      // Low-frequency rumble
      const bassFreq = 35 + intensity * 25;
      const bass = Math.sin(2 * Math.PI * bassFreq * t) * 0.5 * intensity;

      // Mid-frequency body with descending resonance
      const midFreq = (120 + intensity * 80) * Math.exp(-t * 2);
      const mid = Math.sin(2 * Math.PI * midFreq * t) * 0.3;

      // Shaped noise (filtered for more natural sound)
      const rawNoise = Math.random() * 2 - 1;
      const noiseCutoff = Math.max(0.05, 0.6 * Math.exp(-t * (3 - intensity)));
      lpState1 = this._lpf(lpState1, rawNoise, noiseCutoff);
      // Second pass for steeper rolloff
      lpState2 = this._lpf(lpState2, lpState1, noiseCutoff * 0.8);
      const shapedNoise = lpState2 * 2.5;

      // === PHASE 3: Debris tail (crackles) ===
      const debrisEnv = Math.max(0, Math.exp(-t * 1.5) - Math.exp(-t * 4)) * intensity;
      const crackle = (Math.random() > 0.97 ? (Math.random() * 2 - 1) * 0.8 : 0);

      // Mix all phases
      const sample = transient * 0.6 +
                     blastEnv * (bass + mid + shapedNoise * 0.7) * 0.55 +
                     debrisEnv * crackle * 0.3;

      data[i] = this._softClip(sample * (0.5 + intensity * 0.3));
    }

    return buffer;
  }

  /**
   * Generate a city atomic detonation sound - nuclear explosion SFX to match
   * the mushroom cloud animation. Multi-phase synthesis with heavy emphasis
   * on visceral impact, aggressive mid-range roar, and sustained deep rumble:
   *   Phase 1: Concussion bang (0-25ms) - massive double-hit transient
   *   Phase 2: Blast wave (5-400ms) - saturated pressure wave sweep
   *   Phase 3: Thermonuclear roar (30ms-2.2s) - aggressive fire roar + sub-bass
   *   Phase 4: Shockwave return / collapse (250ms-2.2s) - structural crumble
   *   Phase 5: Mushroom cloud rumble (0.5-3.0s) - heavy rolling thunder
   *   Phase 6: Fallout decay (2.0-3.5s) - deep resonance + wind
   * Duration: 3.5s to match the mushroom cloud animation.
   */
  _genCityDestroySound(ctx, sampleRate) {
    const duration = 3.5;
    const buffer = ctx.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);
    let lpState1 = 0, lpState2 = 0, lpState3 = 0, lpState4 = 0, lpState5 = 0;

    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;

      // === PHASE 1: Concussion bang (0-25ms) ===
      // Massive double-hit transient — initial crack + bass punch
      const flashEnv = t < 0.025 ? (t < 0.002 ? t / 0.002 : Math.exp(-(t - 0.002) * 80)) : 0;
      const flash = flashEnv * (
        Math.sin(2 * Math.PI * 60 * t) * 0.8 +        // Deep bass punch
        Math.sin(2 * Math.PI * 150 * t) * 0.5 +        // Mid punch
        (Math.random() * 2 - 1) * 0.7                   // Noise crack
      ) * 2.0;
      // Secondary concussion hit at ~12ms for "double bang" feel
      const bang2Env = t > 0.010 && t < 0.035 ? Math.exp(-(t - 0.010) * 100) : 0;
      const bang2 = bang2Env * Math.sin(2 * Math.PI * 45 * t) * 1.2;

      // === PHASE 2: Blast wave (5-400ms) ===
      // Massive saturated pressure wave with wider frequency content
      const blastEnv = t > 0.005 && t < 0.45 ?
        (t < 0.015 ? (t - 0.005) / 0.010 : 1.0) * Math.exp(-(t - 0.005) * 4) : 0;
      const blastFreq = 300 * Math.exp(-t * 6) + 25;
      const blastFM = Math.sin(2 * Math.PI * blastFreq * 3.0 * t) * 0.6;
      const blastOsc = Math.sin(2 * Math.PI * blastFreq * t + blastFM);
      // Heavy compressed noise burst
      const blastNoise = Math.random() * 2 - 1;
      const blastNoiseCut = Math.max(0.06, 0.8 * Math.exp(-t * 4));
      lpState1 = this._lpf(lpState1, blastNoise, blastNoiseCut);
      const blast = blastEnv * (blastOsc * 0.6 + this._softClip(lpState1 * 4.0) * 0.6);

      // === PHASE 3: Thermonuclear roar (30ms-2.2s) ===
      // Aggressive fire roar — boosted mid-frequencies and saturated noise
      const roarEnv = t > 0.03 ? Math.exp(-(t - 0.03) * 0.9) : 0;

      // Sub-bass nuclear rumble (12-40Hz) — deeper and louder
      const subFreq = 14 + 20 * Math.exp(-t * 1.5);
      const subBass = Math.sin(2 * Math.PI * subFreq * t) * 0.7;
      const subBass2 = Math.sin(2 * Math.PI * subFreq * 0.5 * t) * 0.5;
      // Third sub-harmonic for chest-shaking depth
      const subBass3 = Math.sin(2 * Math.PI * subFreq * 0.25 * t) * 0.3;

      // Aggressive mid-freq fire roar — wider filter band, higher gain
      const roarNoise = Math.random() * 2 - 1;
      const roarCut = 0.25 + 0.35 * Math.exp(-t * 1.0);
      lpState2 = this._lpf(lpState2, roarNoise, roarCut);
      lpState3 = this._lpf(lpState3, lpState2, roarCut * 0.6);
      // Saturate the roar for aggressive character
      const fireRoar = this._softClip(lpState3 * 5.0) * 0.8;

      // Dense crackling debris
      const crackle = Math.random() > 0.97 ? (Math.random() * 2 - 1) * 0.9 : 0;
      const crackleEnv = t > 0.05 && t < 2.0 ? Math.exp(-(t - 0.05) * 1.5) : 0;

      const roar = roarEnv * (subBass + subBass2 + subBass3 + fireRoar + crackle * crackleEnv);

      // === PHASE 4: Shockwave return / structural collapse (250ms-2.2s) ===
      const returnEnv = t > 0.25 && t < 2.2 ?
        Math.sin(Math.min(Math.PI, (t - 0.25) / 0.25 * Math.PI)) * Math.exp(-(t - 0.25) * 1.4) * 0.7 : 0;
      const returnFreq = 80 * Math.exp(-(t - 0.25) * 2.5) + 20;
      const returnWave = Math.sin(2 * Math.PI * returnFreq * t);
      // Heavy structural collapse crumble
      const collapseNoise = Math.random() * 2 - 1;
      lpState4 = this._lpf(lpState4, collapseNoise, 0.12);
      const collapse = returnEnv * (returnWave * 0.7 + this._softClip(lpState4 * 3.0) * 0.5);

      // === PHASE 5: Mushroom cloud rumble (0.5-3.0s) ===
      // Heavy rolling thunder — louder, longer, with more body
      const cloudEnv = t > 0.5 && t < 3.0 ?
        Math.sin(Math.min(Math.PI, (t - 0.5) / 0.4 * Math.PI * 0.5)) * Math.exp(-(t - 0.5) * 0.7) * 0.6 : 0;
      // Multi-frequency undulating rumble
      const cloudFreq1 = 25 + 10 * Math.sin(2 * Math.PI * 0.5 * t);
      const cloudFreq2 = 40 + 6 * Math.sin(2 * Math.PI * 0.8 * t);
      const cloudRumble = Math.sin(2 * Math.PI * cloudFreq1 * t) * 0.6 +
                          Math.sin(2 * Math.PI * cloudFreq2 * t) * 0.35;
      // Rolling thunder crackle — denser
      const thunderCrackle = Math.random() > 0.94 ? (Math.random() * 2 - 1) * 0.6 : 0;
      // Rumble noise layer
      const rumbleNoise = Math.random() * 2 - 1;
      lpState5 = this._lpf(lpState5, rumbleNoise, 0.06);
      const cloud = cloudEnv * (cloudRumble + thunderCrackle + lpState5 * 1.5);

      // === PHASE 6: Fallout decay (2.0-3.5s) ===
      const falloutEnv = t > 2.0 ? Math.exp(-(t - 2.0) * 0.9) * 0.35 : 0;
      const eerie1 = Math.sin(2 * Math.PI * 38 * t);
      const eerie2 = Math.sin(2 * Math.PI * 40 * t);
      const windRaw = Math.random() * 2 - 1;
      const windLp = this._lpf(0, windRaw, 0.04);
      const wind = (windRaw - windLp) * 0.2 * Math.exp(-(t - 2.0) * 0.7);
      const fallout = falloutEnv * ((eerie1 + eerie2) * 0.5 + (t > 2.0 ? wind : 0));

      // === Final mix — hotter levels throughout ===
      const sample = flash + bang2 +
                     blast * 1.0 +
                     roar * 0.85 +
                     collapse * 0.65 +
                     cloud +
                     fallout;

      // Multi-tap pseudo-reverb for bigger space
      const rev1 = i - Math.floor(sampleRate * 0.07);
      const rev2 = i - Math.floor(sampleRate * 0.13);
      const rev3 = i - Math.floor(sampleRate * 0.21);
      const reverb = (rev1 > 0 ? data[rev1] * 0.14 : 0) +
                     (rev2 > 0 ? data[rev2] * 0.09 : 0) +
                     (rev3 > 0 ? data[rev3] * 0.05 : 0);

      data[i] = this._softClip((sample + reverb) * 0.65);
    }

    return buffer;
  }

  /**
   * Generate a wave complete fanfare - triumphant ascending arpeggio with
   * harmonics, reverb tail, and celebratory sparkle.
   * Inspired by classic arcade victory jingles.
   */
  _genWaveCompleteSound(ctx, sampleRate) {
    const duration = 1.2;
    const buffer = ctx.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);

    // C major arpeggio + octave + triumphant fifth
    const notes = [
      { freq: 523.25, start: 0.0,  len: 0.25 },  // C5
      { freq: 659.25, start: 0.12, len: 0.25 },  // E5
      { freq: 783.99, start: 0.24, len: 0.25 },  // G5
      { freq: 1046.5, start: 0.36, len: 0.35 },  // C6
      { freq: 1318.5, start: 0.50, len: 0.45 },  // E6 (triumphant peak)
    ];

    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;
      let sample = 0;

      for (const note of notes) {
        const nt = t - note.start;
        if (nt < 0 || nt > note.len + 0.3) continue;

        // ADSR envelope for each note
        const noteEnv = this._adsr(nt, 0.008, 0.05, 0.7, 0.25, note.len + 0.25);

        // Rich tone: fundamental + 2nd harmonic + 3rd (bell-like)
        const f = note.freq;
        const fundamental = Math.sin(2 * Math.PI * f * t) * 0.5;
        const harmonic2 = Math.sin(2 * Math.PI * f * 2 * t) * 0.2;
        const harmonic3 = Math.sin(2 * Math.PI * f * 3 * t) * 0.08;

        // Slight vibrato on sustained notes
        const vibrato = 1 + 0.003 * Math.sin(2 * Math.PI * 5.5 * nt);

        sample += noteEnv * (fundamental + harmonic2 + harmonic3) * vibrato;
      }

      // Sparkle overlay (high-frequency shimmer)
      if (t > 0.4 && t < 1.0) {
        const sparkleEnv = Math.exp(-(t - 0.4) * 3) * 0.08;
        sample += sparkleEnv * Math.sin(2 * Math.PI * 3000 * t + Math.sin(2 * Math.PI * 7 * t) * 3);
      }

      // Simple reverb tail using delay line (pseudo-reverb)
      const reverbIdx = i - Math.floor(sampleRate * 0.07);
      if (reverbIdx > 0) {
        sample += data[reverbIdx] * 0.15;
      }

      data[i] = this._softClip(sample * 0.35);
    }

    return buffer;
  }

  /**
   * Generate an "empty click" sound - mechanical dry click with metallic ring.
   * Short, percussive, clearly communicates "denied" without being annoying.
   */
  _genEmptyClickSound(ctx, sampleRate) {
    const duration = 0.15;
    const buffer = ctx.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;

      // Sharp mechanical click transient
      const clickEnv = Math.exp(-t * 80);
      const click = clickEnv * Math.sin(2 * Math.PI * 1200 * t) * 0.5;

      // Metallic ring (two detuned high tones)
      const ringEnv = Math.exp(-t * 25);
      const ring1 = Math.sin(2 * Math.PI * 600 * t) * 0.2;
      const ring2 = Math.sin(2 * Math.PI * 630 * t) * 0.15;

      // Low thud
      const thudEnv = Math.exp(-t * 50);
      const thud = thudEnv * Math.sin(2 * Math.PI * 150 * t) * 0.3;

      data[i] = (click + ringEnv * (ring1 + ring2) + thud) * 0.35;
    }

    return buffer;
  }

  /**
   * Generate ambient background audio - atmospheric sci-fi tension soundtrack.
   * Multi-layered: deep drone, pulsing sub-bass, eerie pads, radar-like pings,
   * and filtered noise. Seamlessly loops over 6 seconds.
   */
  _genAmbientSound(ctx, sampleRate) {
    const duration = 6.0; // 6 seconds for more variety before loop
    const buffer = ctx.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);
    let lpState = 0;

    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;
      const phase = t / duration; // 0 to 1

      // Smooth loop crossfade envelope
      const loopFade = Math.sin(phase * Math.PI);
      const loopEnv = 0.7 + 0.3 * loopFade;

      // Deep bass drone with slow frequency wobble
      const droneFreq = 38 + 4 * Math.sin(2 * Math.PI * 0.12 * t);
      const drone = Math.sin(2 * Math.PI * droneFreq * t) * 0.2;

      // Sub-harmonic pulse (heartbeat-like)
      const subPulse = Math.sin(2 * Math.PI * 18 * t) * 0.12 *
                       (0.3 + 0.7 * Math.pow(Math.sin(2 * Math.PI * 0.5 * t), 2));

      // Eerie pad: two detuned tones creating slow beating
      const pad1 = Math.sin(2 * Math.PI * 110 * t) * 0.04;
      const pad2 = Math.sin(2 * Math.PI * 112 * t) * 0.04;
      const padLfo = 0.3 + 0.7 * Math.sin(2 * Math.PI * 0.08 * t);
      const pad = (pad1 + pad2) * padLfo;

      // Ethereal high whisper with LFO
      const whisperLfo = 0.5 + 0.5 * Math.sin(2 * Math.PI * 0.2 * t);
      const whisper = Math.sin(2 * Math.PI * 330 * t) * 0.015 * whisperLfo;

      // Filtered noise texture (wind-like)
      const rawNoise = Math.random() * 2 - 1;
      const noiseCut = 0.03 + 0.02 * Math.sin(2 * Math.PI * 0.15 * t);
      lpState = this._lpf(lpState, rawNoise, noiseCut);
      const windNoise = lpState * 0.8;

      // Subtle radar-like ping at regular intervals
      const pingT = t % 3.0; // Every 3 seconds
      const pingEnv = pingT < 0.1 ? Math.exp(-pingT * 30) * 0.06 : 0;
      const ping = pingEnv * Math.sin(2 * Math.PI * 880 * pingT);

      const sample = (drone + subPulse + pad + whisper + windNoise + ping) * loopEnv * 0.4;
      data[i] = Math.max(-1, Math.min(1, sample));
    }

    return buffer;
  }

  /**
   * Generate a MIRV split sound - sharp cracking sound with dispersal whoosh.
   * Plays when MIRV missile separates into multiple warheads.
   */
  _genMirvSplitSound(ctx, sampleRate) {
    const duration = 0.35;
    const buffer = ctx.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);
    let lpState = 0;

    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;

      // Sharp crack transient
      const crackEnv = t < 0.015 ? 1.0 : Math.exp(-(t - 0.015) * 40);
      const crack = crackEnv * (Math.random() * 2 - 1) * 0.6;

      // Dispersal whoosh (ascending frequency)
      const whooshEnv = Math.exp(-t * 5) * (t > 0.01 ? 1 : 0);
      const whooshFreq = 200 + t * 3000;
      const rawN = Math.random() * 2 - 1;
      lpState = this._lpf(lpState, rawN, Math.min(0.9, whooshFreq / sampleRate * 4));
      const whoosh = whooshEnv * lpState * 1.5;

      // Metallic ping (warhead separation)
      const pingEnv = Math.exp(-t * 20);
      const ping = pingEnv * Math.sin(2 * Math.PI * 1800 * t) * 0.15;

      data[i] = this._softClip((crack + whoosh + ping) * 0.45);
    }

    return buffer;
  }

  /**
   * Generate a missile incoming warning sound - descending whistle/whine.
   * Subtle but recognizable, adds tension when enemies are close.
   */
  _genMissileIncomingSound(ctx, sampleRate) {
    const duration = 0.6;
    const buffer = ctx.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;

      // Descending whistle
      const env = this._adsr(t, 0.02, 0.1, 0.6, 0.15, duration);
      const freq = 1200 * Math.exp(-t * 2.5) + 200;

      // Dual oscillator for richer whistle
      const osc1 = Math.sin(2 * Math.PI * freq * t);
      const osc2 = Math.sin(2 * Math.PI * freq * 1.01 * t); // Slight detune

      // Subtle tremolo
      const tremolo = 0.8 + 0.2 * Math.sin(2 * Math.PI * 12 * t);

      data[i] = env * (osc1 + osc2) * 0.5 * tremolo * 0.2;
    }

    return buffer;
  }

  /**
   * Generate a score tick sound - short blip for score counting animation.
   */
  _genScoreTickSound(ctx, sampleRate) {
    const duration = 0.06;
    const buffer = ctx.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;
      const env = Math.exp(-t * 60);
      // Clean high-pitched blip
      data[i] = env * Math.sin(2 * Math.PI * 1400 * t) * 0.2;
    }

    return buffer;
  }

  /**
   * Generate a game over sound - dramatic descending sequence conveying defeat.
   */
  _genGameOverSound(ctx, sampleRate) {
    const duration = 2.0;
    const buffer = ctx.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
    const data = buffer.getChannelData(0);

    // Descending minor notes: E4, D4, C4, then low rumble
    const notes = [
      { freq: 329.63, start: 0.0, len: 0.4 },   // E4
      { freq: 293.66, start: 0.35, len: 0.4 },   // D4
      { freq: 261.63, start: 0.70, len: 0.5 },   // C4
      { freq: 196.00, start: 1.05, len: 0.7 },   // G3 (low, final)
    ];

    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;
      let sample = 0;

      for (const note of notes) {
        const nt = t - note.start;
        if (nt < 0 || nt > note.len + 0.3) continue;

        const noteEnv = this._adsr(nt, 0.01, 0.15, 0.5, 0.3, note.len + 0.25);

        // Dark tone: fundamental + minor third harmonic
        const f = note.freq;
        const fundamental = Math.sin(2 * Math.PI * f * t) * 0.4;
        const harm2 = Math.sin(2 * Math.PI * f * 2 * t) * 0.15;
        const minorThird = Math.sin(2 * Math.PI * f * 1.2 * t) * 0.1; // Minor feel

        sample += noteEnv * (fundamental + harm2 + minorThird);
      }

      // Deep rumble at end
      if (t > 1.2) {
        const rumbleEnv = Math.exp(-(t - 1.2) * 2.5) * 0.3;
        sample += rumbleEnv * Math.sin(2 * Math.PI * 40 * t);
      }

      // Pseudo-reverb
      const reverbIdx = i - Math.floor(sampleRate * 0.08);
      if (reverbIdx > 0) {
        sample += data[reverbIdx] * 0.2;
      }

      data[i] = this._softClip(sample * 0.35);
    }

    return buffer;
  }
}
